import { IShip, IShipData, IShipUser, IShipUserCrewRole } from "../../Types"
import { predefined, ShipDataViewData } from "../ShipData/Context"
import type { TranslateFn } from "../ShipData/Context"

// Re-export the shared ship-data helpers (now owned by the interactive ShipData/Context)
// so existing PrintShip imports keep working while the single source of truth lives on
// the non-print side.
export { predefined }
export type { TranslateFn }

// Pure, React-free print-layout logic for PrintShipView. Grouped here so it can be
// unit-tested in isolation (see Tests/PublicTests/Views/PrintShipView.Data.test.tsx).
// Only the genuinely print-only helpers remain here; predefinedText / engineAt /
// crewRoleText / userLevelText moved to ShipDataViewData and are re-exposed below so
// callers that reach for them via PrintShipViewData keep working.
export const PrintShipViewData = {
  // The `data` prop is either a raw IShipData (identified by the `mmsi` field) or an
  // IShip whose ship data hangs off `.shipData`.
  resolveShipData(data: IShip | IShipData): { shipData?: IShipData; ship?: IShip } {
    const shipData: IShipData | undefined = "mmsi" in data ? (data as IShipData) : (data as IShip).shipData;
    const ship: IShip | undefined = "mmsi" in data ? undefined : (data as IShip);
    return { shipData, ship };
  },

  // Active crew, ordered owner → skipper → crew → none, then by name.
  sortShipUsers(users: IShipUser[]): IShipUser[] {
    return users.filter((user) => user.active !== 0).toSorted((a, b) => {
      const roleOrder: Record<string, number> = { [IShipUserCrewRole.owner]: 0, [IShipUserCrewRole.skipper]: 1, [IShipUserCrewRole.crew]: 2, [IShipUserCrewRole.none]: 3 };
      const roleDiff = (roleOrder[a.shipUserCrewRole] ?? 3) - (roleOrder[b.shipUserCrewRole] ?? 3);
      return roleDiff !== 0 ? roleDiff : (a.name ?? "").localeCompare(b.name ?? "");
    });
  },

  // Shared derivations, re-exposed from ShipDataViewData so the Print ship views keep
  // referencing them through PrintShipViewData without duplicating the logic.
  predefinedText: ShipDataViewData.predefinedText,
  engineAt: ShipDataViewData.engineAt,
  crewRoleText: ShipDataViewData.crewRoleText,
  userLevelText: ShipDataViewData.userLevelText,
};
